import { useState } from "react";
import { FacebookIcon } from '../icons/common/facebook';
import { TwitterIcon } from '../icons/common/twitter';
import { WhatsAppIcon } from '../icons/common/whatsapp';
import { Copy, Check, Share2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

interface ShareSectionProps {
    title?: string;
    subtitle?: string;
    className?: string;
}

export function ShareSection({
    title = "Compartilhar",
    subtitle = "Envie este conteúdo para alguém",
    className = ""
}: ShareSectionProps) {
    const [copied, setCopied] = useState(false);
    const currentUrl = window.location.href;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(currentUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            // silent
        }
    };

    return (
        <div className={`mt-16 flex flex-col sm:flex-row items-center justify-between gap-6 py-8 border-t border-border-default dark:border-border-default-dark ${className}`}>
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/5 dark:bg-secondary/5 flex items-center justify-center text-primary dark:text-secondary">
                    <Share2 size={18} />
                </div>
                <div>
                    <div className="text-[10px] font-black text-primary dark:text-on-surface-dark uppercase tracking-widest mb-0.5">{title}</div>
                    <div className="text-[9px] text-on-surface-subtle uppercase tracking-wider">{subtitle}</div>
                </div>
            </div>

            <div className="flex gap-3">
                <button
                    onClick={handleCopy}
                    title="Copiar Link"
                    className={`w-10 h-10 flex items-center justify-center rounded-xl cursor-pointer text-white transition-all shadow-sm border-none relative overflow-hidden group/copy ${copied ? 'bg-green-600' : 'bg-gray-800 dark:bg-gray-700'}`}
                >
                    <AnimatePresence mode="wait" initial={false}>
                        {copied ? (
                            <motion.div
                                key="check"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                            >
                                <Check size={18} />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="copy"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                            >
                                <Copy size={16} />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </button>

                <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Facebook"
                    className="w-10 h-10 flex items-center justify-center bg-[#3B5998] rounded-xl text-white hover:-translate-y-1 transition-transform shadow-sm [&_svg]:h-5 [&_svg]:fill-white"
                >
                    <FacebookIcon />
                </a>

                <a
                    href={`https://x.com/intent/post?url=${encodeURIComponent(currentUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Twitter - X"
                    className="w-10 h-10 flex items-center justify-center bg-black rounded-xl text-white hover:-translate-y-1 transition-transform shadow-sm [&_svg]:h-4 [&_svg]:fill-white"
                >
                    <TwitterIcon />
                </a>

                <a
                    href={`https://wa.me?text=${encodeURIComponent(currentUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-action="share/whatsapp/share"
                    title="WhatsApp"
                    className="w-10 h-10 flex items-center justify-center bg-[#25D366] rounded-xl text-white hover:-translate-y-1 transition-transform shadow-sm [&_svg]:h-5 [&_svg]:fill-white"
                >
                    <WhatsAppIcon />
                </a>
            </div>
        </div>
    );
}
